import type { WhoAmIResponse } from '@carculate/api-client';

import { isFitterOnlyGated } from '@/features/fitter/fitterAccess';

import { isInsurerUser } from './roles';

/**
 * Story `fe-be-cursusmodus-hersteller-mvp` (AC-A3.3) — de ENIGE plek in de FE die whoami
 * `training` interpreteert. Geen ander bestand vergelijkt zelf op `training`/`.active` of doet
 * een eigen hersteller-toets voor de cursussen (scan `training/__tests__/trainingMode.test.ts`).
 *
 * ⚠ Waarom dit bestand in `src/auth/` staat en niet in `features/training/` (afwijking van het
 * anker in de story, zie het dev-rapport): de route-gate van `/cursussen`, de landing na het
 * inloggen (`DefaultLanding`) en het titelvoorvoegsel draaien in `routes.tsx` en de
 * `AuthProvider`. Die zitten in de keten van `/monteur`, en daar mag geen kantoor-feature in
 * (`features/fitter/fitterBundleRules.ts`). Een pure poortmodule hoort — net als
 * `restrictedRepairer.ts` en `claimFilterAudience.ts` — in `src/auth/`.
 */

/**
 * Staat de cursusmodus aan? Alleen `training.active === true` telt (fail-closed). Een afwezige
 * sleutel, `null`, `{}`, `"true"` en `1` zijn allemaal UIT.
 */
export function isTrainingActive(user: WhoAmIResponse | null | undefined): boolean {
    return user?.training?.active === true;
}

/**
 * Is dit een trainingsomgeving — actief óf geblokkeerd? `true` zodra whoami de sleutel
 * `training` draagt (een object), ongeacht `active`. Voedt de balk (paars of rood, AC-A4.1/A4.2)
 * en het titelvoorvoegsel `[Training]` (AC-A4.3). Op productie ontbreekt de sleutel.
 */
export function isTrainingEnvironment(user: WhoAmIResponse | null | undefined): boolean {
    const flag = user?.training;
    return flag !== null && flag !== undefined && typeof flag === 'object';
}

/**
 * Mag deze gebruiker de cursussen gebruiken (tegel, route, motor, landing)? Alleen een
 * hersteller-VESTIGING op een actieve trainingsomgeving, geen alleen-monteur en geen
 * verzekeraar. HK (`RepairerHQ`), `System`, `RegularUser` en monteurs: nee (PO-besluit MVP).
 */
export function canUseCourses(user: WhoAmIResponse | null | undefined): boolean {
    if (!user) return false;
    if (!isTrainingActive(user)) return false;
    if (user.userContextType !== 'RepairerLocation') return false;
    if (isFitterOnlyGated(user)) return false;
    if (isInsurerUser(user)) return false;
    return true;
}

/** Welke balk staat er bovenaan de `Chrome` (AC-A4)? `null` = geen balk (productie). */
export type TrainingBannerKind = 'active' | 'blocked' | null;

/** De balk-keuze uit whoami: paars bij actief, rood bij een geblokkeerde trainingsomgeving. */
export function trainingBannerKind(user: WhoAmIResponse | null | undefined): TrainingBannerKind {
    if (!isTrainingEnvironment(user)) return null;
    return isTrainingActive(user) ? 'active' : 'blocked';
}
